import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function ReimposeModal({ screen, customers, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    newCustomerName: '',
    newItemDescription: ''
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [artworkFile, setArtworkFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const value = e.target.value;
    setSelectedCustomerId(value);
    const customer = customers.find(c => c.id === value);
    if (customer) {
      setFormData(prev => ({ ...prev, newCustomerName: customer.name }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      setArtworkFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.newCustomerName || !formData.newItemDescription) {
      setError('Customer name and item description are required');
      return;
    }

    if (!artworkFile) {
      setError('Please upload new artwork PDF');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('newCustomerName', formData.newCustomerName);
      formDataToSend.append('newItemDescription', formData.newItemDescription);
      formDataToSend.append('newArtworkPdf', artworkFile);

      const response = await fetch(`${API_URL}/silkscreens/${screen.id}/reimpose`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reimpose screen');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#9C27B0',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          🔄 Re-impose Screen
        </h2>

        <div style={{
          background: '#f3e5f5',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '0.9rem',
          color: '#6a1b9a'
        }}>
          <strong>Current:</strong> {screen.screenCode}
          <br />
          Customer: {screen.currentCustomerName}
          <br />
          Description: {screen.currentItemDescription}
        </div>

        <div style={{
          background: '#fff3e0',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '0.85rem',
          color: '#e65100'
        }}>
          ⚠️ <strong>Note:</strong> Re-imposing will move the current artwork to history and replace it with new artwork. The screen code will remain the same.
        </div>

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.95rem'
            }}>
              New Customer *
            </label>
            <select
              value={selectedCustomerId}
              onChange={handleCustomerChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                cursor: 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#9C27B0'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            >
              <option value="">Select a customer...</option>
              {customers && customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.95rem'
            }}>
              New Item Description *
            </label>
            <textarea
              name="newItemDescription"
              value={formData.newItemDescription}
              onChange={handleChange}
              placeholder="e.g., New Logo Design 2026"
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              onFocus={(e) => e.target.style.borderColor = '#9C27B0'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.95rem'
            }}>
              New Artwork PDF (Max 4MB) *
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed #9C27B0',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer',
                background: '#fafafa'
              }}
            />
            {artworkFile && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: '#e8f5e9',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: '#2e7d32'
              }}>
                ✓ {artworkFile.name} ({(artworkFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#e0e0e0')}
              onMouseLeave={(e) => e.target.style.background = '#f5f5f5'}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: loading ? '#b0bec5' : '#9C27B0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#8E24AA')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#9C27B0')}
            >
              {loading ? 'Re-imposing...' : 'Re-impose Screen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReimposeModal;
